import type { Dictionary } from './en.js';

/**
 * Bangla. Typed as `Dictionary`, so a missing or misspelled key fails
 * typecheck rather than silently falling back at runtime.
 *
 * Note the numerals stay Western (see @aj/tokens `numeralSystem`) — "৪,৮২০"
 * loses the punch of a Power Level and doesn't travel on share cards.
 */
export const bn: Dictionary = {
  'app.name': 'অগ্রযাত্রা',
  'app.tagline': 'অভ্যাস গড়ো। পড়া ট্র্যাক করো। নিজেকে এগিয়ে নাও।',

  'crash.title': 'এই স্ক্রিনে কিছু একটা সমস্যা হয়েছে',
  'crash.safe': 'তোমার রেকর্ড নিরাপদ আছে। যা লিখেছো কিছুই হারায়নি — শুধু এই স্ক্রিনটা আঁকতে পারেনি।',
  'crash.reload': 'অ্যাপ রিলোড করো',
  'crash.back': 'আবার চেষ্টা করো',
  'crash.details': 'টেকনিক্যাল বিস্তারিত',

  'nav.today': 'আজ',
  'nav.jatra': 'যাত্রা',
  'nav.khata': 'খাতা',
  'nav.me': 'আমি',
  'nav.comingSoon': 'শীঘ্রই আসছে',

  'now.open': 'এখন',
  'now.title': 'এখন',
  'now.startFocus': 'ফোকাস শুরু করো',
  'now.logEntry': 'যা পড়েছি লিখি',
  'now.quickTask': 'দ্রুত কাজ',
  'now.newHabit': 'নতুন অভ্যাস',
  'now.newGoal': 'নতুন লক্ষ্য',
  'now.soon': 'শীঘ্রই',
  'now.close': 'বন্ধ করো',

  'log.subject': 'বিষয়',
  'log.newSubject': 'নতুন',
  'log.subjectName': 'বিষয়ের নাম',
  'log.howLong': 'কতক্ষণ',
  'log.otherMinutes': 'অন্য',
  'log.minutesName': 'মিনিট',
  'log.submit': 'লিখে রাখো',
  'log.today': 'আজকের হিসাব',
  'log.nothingYet': 'আজ এখনো কিছু লেখা হয়নি।',
  'log.moreEntries': 'আরও {count}টি',
  'log.timed': 'টাইমার',
  'log.review': 'রিভিশন',
  'log.entry': '{subject} — {minutes}',

  'greeting.morning': 'শুভ সকাল',
  'greeting.afternoon': 'শুভ দুপুর',
  'greeting.evening': 'শুভ সন্ধ্যা',
  'greeting.night': 'এখনো জেগে আছো?',

  'home.today': 'আজ',
  'home.addHabit': 'অভ্যাস যোগ করো',
  'home.addTask': 'কাজ যোগ করো',
  'home.empty.title': 'এখনো কিছু নেই',
  'home.empty.body': 'প্রথম অভ্যাসটি যোগ করো, আজ থেকেই স্ট্রিক শুরু হোক।',
  'home.habitName': 'অভ্যাসের নাম',
  'home.tasks': 'কাজ',
  'home.taskName': 'কাজের নাম',
  'home.noTasks': 'তালিকা খালি। আজ কী পড়বে লিখে রাখো।',
  'home.openTasks': { one: '{count}টি বাকি', other: '{count}টি বাকি' },
  'home.overdue': 'সময় পেরিয়েছে',
  'home.moreTasks': 'আরও {count}টি',
  'home.daysLeft': '{count} দিন বাকি',
  'goal.partsOf': '{total}-এর {done}',
  'home.streakAtRisk': 'স্ট্রিক এখনো টিকে আছে — একটি টিক দিলেই নিশ্চিত।',

  'streak.days': { one: '{count} দিনের স্ট্রিক', other: '{count} দিনের স্ট্রিক' },
  'streak.none': 'এখনো স্ট্রিক নেই',

  'power.label': 'পাওয়ার',
  'power.level': 'পাওয়ার লেভেল',

  'rank.student.novice': 'নবীন',
  'rank.student.aspirant': 'প্রত্যাশী',
  'rank.student.scholar': 'পণ্ডিত',
  'rank.student.elite': 'এলিট',
  'rank.student.master': 'মাস্টার',
  'rank.student.grandmaster': 'গ্র্যান্ডমাস্টার',
  'rank.student.legend': 'কিংবদন্তি',
  'rank.student.nationalTopper': 'ন্যাশনাল টপার',

  'rank.general.novice': 'নবাগত',
  'rank.general.aspirant': 'স্থির',
  'rank.general.scholar': 'প্রতিশ্রুত',
  'rank.general.elite': 'অভিজ্ঞ',
  'rank.general.master': 'দক্ষ',
  'rank.general.grandmaster': 'সুশৃঙ্খল',
  'rank.general.legend': 'নিবেদিত',
  'rank.general.nationalTopper': 'আজীবন',

  'title.student.nightOwl': 'নিশাচর',
  'title.student.earlyBird': 'ভোরের পাখি',
  'title.student.ironWill': 'ইস্পাত সংকল্প',
  'title.student.unbreakable': 'অটুট',
  'title.student.bookworm': 'বইপোকা',
  'title.student.revisionMaster': 'রিভিশন মাস্টার',
  'title.student.focusedMind': 'একাগ্র মন',
  'title.student.pomodoroPro': 'পোমোডোরো প্রো',
  'title.student.marathonStudier': 'ম্যারাথন পাঠক',
  'title.student.phoenix': 'ফিনিক্স',

  'title.general.nightOwl': 'নিশাচর',
  'title.general.earlyBird': 'ভোরে ওঠা',
  'title.general.ironWill': 'ইস্পাত সংকল্প',
  'title.general.unbreakable': 'অটুট',
  'title.general.bookworm': 'গভীর কাজ',
  'title.general.revisionMaster': 'পুনরাবৃত্তি',
  'title.general.focusedMind': 'একাগ্র',
  'title.general.pomodoroPro': 'স্প্রিন্টার',
  'title.general.marathonStudier': 'দীর্ঘ পথিক',
  'title.general.phoenix': 'প্রত্যাবর্তন',

  'coach.path.title': 'কীসের জন্য প্রস্তুতি নিচ্ছো?',
  'coach.path.body': 'এক ট্যাপ। এটা ঠিক করে আমরা কী নামে ডাকবো আর কী আগে থেকে সাজিয়ে দেবো।',
  'path.hsc': 'এইচএসসি',
  'path.ssc': 'এসএসসি',
  'path.admission': 'ভর্তি পরীক্ষা',
  'path.university': 'বিশ্ববিদ্যালয়',
  'path.other': 'অন্য কিছু',

  'goal.presets': 'তৈরি তালিকা থেকে শুরু করো',

  'stats.studyToday': 'আজ পড়েছো',
  'stats.studyTotal': 'মোট পড়া',
  'stats.achievements': { one: '{count} অর্জন', other: '{count} অর্জন' },
  'stats.habitsDone': '{total} এর মধ্যে {done} সম্পন্ন',

  'duration.hoursMinutes': '{hours}ঘ {minutes}মি',
  'duration.minutes': '{minutes}মি',
  'duration.hours': '{hours}ঘ',

  'action.save': 'সেভ',
  'action.cancel': 'বাতিল',
  'action.delete': 'মুছে ফেলো',
  'action.done': 'সম্পন্ন',
  'action.retry': 'আবার চেষ্টা করো',
  'action.share': 'শেয়ার',
  'action.install': 'ফোনে ইনস্টল করো',
  'action.add': 'যোগ করো',

  'home.greetingWith': '{greeting}, {name}',
  'home.percent': '{value}%',
  'home.kept': {
    one: '{count} দিনে দেওয়া কথার {percent} রেখেছো',
    other: '{count} দিনে দেওয়া কথার {percent} রেখেছো',
  },
  'home.habits': 'অভ্যাস',
  'home.doneOfTotal': '{done} / {total}',
  'home.dragHandle': '{name} সাজাও — চেপে ধরে টানো, বা অ্যারো কী ব্যবহার করো',
  'home.template': 'স্টার্টার প্যাক নাও',
  'home.templateHint': 'বেশিরভাগ শিক্ষার্থীর ৫টি অভ্যাস — এক ট্যাপে যোগ হবে।',

  'starter.read': '৩০ মিনিট পড়া',
  'starter.math': '১০টি অঙ্ক করা',
  'starter.revise': 'আজকের ক্লাস রিভিশন',
  'starter.sleep': 'রাত ১২টার আগে ঘুম',
  'starter.exercise': '১৫ মিনিট ব্যায়াম',
  'home.newHabit': 'নতুন অভ্যাস',

  'grid.floor': '{name}, {date}: ন্যূনতমটুকু রাখা হয়েছে',
  'grid.done': '{name}, {date}: সম্পন্ন',
  'grid.missed': '{name}, {date}: বাদ পড়েছে',
  'grid.off': '{name}, {date}: নির্ধারিত নয়',
  'grid.future': '{name}, {date}: আসছে',

  'week.dayLabel': '{day}: {total}-এর মধ্যে {done}টি সম্পন্ন',
  'week.dayEmpty': '{day}: কিছু নির্ধারিত নেই',

  'schedule.daily': 'প্রতিদিন',
  'schedule.weekly': { one: 'সপ্তাহে {count} দিন', other: 'সপ্তাহে {count} দিন' },
  'schedule.timesPerWeek': { one: 'সপ্তাহে {count} বার', other: 'সপ্তাহে {count} বার' },

  'power.aria': 'পাওয়ার লেভেল {value}',
  'streak.aria': { one: '{count} দিনের স্ট্রিক', other: '{count} দিনের স্ট্রিক' },
  'streak.bestAria': {
    one: 'সেরা স্ট্রিক {count} দিন',
    other: 'সেরা স্ট্রিক {count} দিন',
  },

  'error.storage': 'এই ডিভাইসে সেভ করা তথ্য খোলা যায়নি।',

  'settings.theme': 'থিম',
  'me.finished': 'সম্পন্ন',
  'me.lifetime': '{days} দিনের রেকর্ড · {duration} · {kept}টি প্রতিশ্রুতি রক্ষা',
  'settings.theme.light': 'লাইট',
  'settings.theme.dark': 'ডার্ক',
  'settings.theme.system': 'সিস্টেম',
  'settings.language': 'ভাষা',

  // ফিরে আসা (§45, §48 #3)। ভাঙা স্ট্রিকের কথা কখনো বলা হয় না।
  'comeback.title': 'আবার স্বাগতম',
  'comeback.missed': {
    one: 'এক দিন বাদ পড়েছে।',
    other: '{count} দিন বাদ পড়েছে।',
  },
  'comeback.invite': 'ফিরে আসাটাই কঠিন কাজ। আজকের দিনটা সাধারণ দিনের চেয়ে বেশি মূল্যবান — ছোট একটা কিছু করলেই যথেষ্ট।',
  'comeback.madeTitle': 'তুমি ফিরে এসেছ',
  'comeback.madeBody': 'এই অংশটাই বেশিরভাগ মানুষ পারে না। আজকের দিন অটুট দিনের চেয়ে বেশি অর্জন করে।',

  // ন্যূনতম — অভ্যাসের সবচেয়ে ছোট রূপ।
  'floor.action': 'শুধু {floor}',
  'floor.kept': 'ন্যূনতম রাখা হয়েছে',
  'floor.label': 'সবচেয়ে ছোট রূপ',
  'floor.hint': 'সবচেয়ে খারাপ দিনেও যতটুকু পারবে — "এক পাতা", "বইটা খোলা"।',
  'floor.optional': 'ঐচ্ছিক',

  // এন্ট্রি সংশোধন। `kind` শুধু এখানেই জিজ্ঞেস করা হয়।
  'log.kind': 'কী ছিল',
  'log.kindLearn': 'প্রথমবার',
  'log.kindReview': 'রিভিশন',
  'log.save': 'সেভ',
  'log.delete': 'মুছে ফেলো',
  'log.edit': '{subject}, {minutes} — সম্পাদনা',

  // খাতা — হিসাবের খাতা।
  'khata.title': 'খাতা',
  'khata.allTracks': 'সব',
  'khata.showEarlier': 'আগের দিনগুলো',
  'khata.summary': '{duration} · {count}টি এন্ট্রি · বেশিরভাগ {track}',
  'khata.quietDays': {
    one: '· ১ দিন চুপচাপ ·',
    other: '· {count} দিন চুপচাপ ·',
  },
  'khata.trackTopic': '{track} · {topic}',
  'khata.outOf': '{value}/{total}',
  'khata.up': '↑ {value}',
  'khata.down': '↓ {value}',
  'khata.today': 'আজ',
  'khata.yesterday': 'গতকাল',
  'khata.empty.title': 'এখনো কিছু লেখা হয়নি',
  'khata.empty.body': 'যা কিছু রেকর্ড করবে সব এখানে জমা হবে। এটাই দিনের প্রমাণের অংশ।',
  'khata.dayTotal': '{minutes}',

  // যাত্রা — তুমি কী হয়ে উঠছ। "স্ট্যাট" নয়।
  'jatra.title': 'যাত্রা',
  'jatra.identity': 'তুমি কী হয়ে উঠছ',
  'jatra.history': 'তোমার রেকর্ড',
  'jatra.keptTitle': 'কথা রেখেছ',
  'jatra.keptBody': '{days} দিনে {promised}টির মধ্যে {kept}টি কথা রেখেছ।',

  'jatra.comebackTitle': 'ফিরে আসা',
  'jatra.comebackNone': 'এখনো কোনো দিন বাদ পড়েনি। যেদিন পড়বে, ফিরে আসার হিসাব এখানেই থাকবে।',
  'jatra.comebackRecord': {
    one: '{gaps} বারের মধ্যে {returns} বার ফিরে এসেছ।',
    other: '{gaps} বারের মধ্যে {returns} বার ফিরে এসেছ।',
  },
  'jatra.comebackLongest': {
    one: '{count} দিন বাদ পড়ার পরেও ফিরে এসেছিলে।',
    other: '{count} দিন বাদ পড়ার পরেও ফিরে এসেছিলে।',
  },
  'jatra.comebackAway': 'আর একবার ফিরলেই সংখ্যাটা বাড়বে।',
  'jatra.streakBest': 'সবচেয়ে লম্বা ধারা: {count} দিন',
  'jatra.empty.title': 'এখনো দেখানোর মতো কিছু নেই',
  'jatra.comebackOf': '{gaps}-এর {returns}',
  'jatra.empty.body': 'আজ পাতায় একটা অভ্যাস যোগ করো। এক সপ্তাহ পেরোলে সব এখানে জমতে শুরু করবে।',

  // যদি-তবে পরিকল্পনা (§48 #1)। ফর্ম নয়, একটা বাক্য।
  'plan.after': 'এরপর',
  'plan.at': 'কোথায়',
  'plan.anchorName': 'কীসের পরে',
  'plan.placeName': 'কোথায়',
  'plan.sentence': '{anchor}-এর পরে, {place}-এ',
  'plan.sentenceAnchorOnly': '{anchor}-এর পরে',
  'plan.sentencePlaceOnly': '{place}-এ',

  'anchor.fajr': 'ফজর',
  'anchor.school': 'স্কুল',
  'anchor.asr': 'আসর',
  'anchor.maghrib': 'মাগরিব',
  'anchor.dinner': 'রাতের খাবার',

  // গ্রিডের চারপাশের সংখ্যাগুলো (§53.6)।
  'stats.kept': 'রেখেছ',
  'stats.keptOf': '{promised}-এর মধ্যে {kept}',
  'stats.streak': 'ধারা',
  'stats.streakDays': { one: '{count} দিন', other: '{count} দিন' },
  'stats.streakBest': 'সেরা {count}',
  'stats.comebacks': 'ফিরে আসা',
  'stats.comebacksOf': '{gaps}-এর মধ্যে {returns}',
  'stats.comebacksNone': 'এখনো দরকার হয়নি',
  'stats.thisWeek': 'এই সপ্তাহ',
  'stats.vsLastWeek': 'গত সপ্তাহের চেয়ে {delta}',
  'stats.sameAsLastWeek': 'গত সপ্তাহের সমান',
  'stats.firstWeek': 'প্রথম সপ্তাহ',
  'stats.keptSoon': '৭ দিন পরে',
  'stats.rowKept': '{value}% রেখেছ',

  // ফলাফল — যেকোনো সংখ্যা যা শিক্ষার্থী রেকর্ড করে (§53.7)।
  'measure.title': 'ফলাফল',
  'measure.add': 'ফলাফল যোগ করো',
  'measure.name': 'কীসের ফলাফল',
  'measure.namePlaceholder': 'ফিজিক্স মক, ক্লাস র‍্যাংক, পাতা',
  'measure.value': 'ফলাফল',
  'measure.outOf': 'এর মধ্যে',
  'measure.track': 'বিষয়',
  'measure.unit': 'একক',
  'measure.unitPlaceholder': 'পাতা, ঘণ্টা, কেজি',
  'measure.shape': 'কীভাবে গোনা হয়',
  'measure.shapeOutOf': 'নম্বর',
  'measure.shapeRank': 'অবস্থান',
  'measure.shapeCount': 'শুধু সংখ্যা',
  'measure.shapeOutOfHint': 'মোটের মধ্যে — ৮০-তে ৬২।',
  'measure.shapeRankHint': 'কত নম্বরে ছিলে — ৩৪০-এ ১২তম।',
  'measure.shapeCountHint': 'শুধু সংখ্যাটা — ৪৫ পাতা।',
  'measure.save': 'রেকর্ড করো',
  'measure.trendLabel': 'সময়ের সাথে {name}',
  'measure.valueOutOf': '{outOf}-এ {value}',
  'measure.valueRank': '{outOf}-এ {value}',
  'measure.deltaPoints': { one: '{count} নম্বর', other: '{count} নম্বর' },
  'measure.deltaPlaces': { one: '{count} ধাপ', other: '{count} ধাপ' },
  'measure.deltaFirst': 'প্রথম রেকর্ড',
  'measure.deltaSame': 'গতবারের সমান',
  'measure.readingOn': '{date} তারিখে {value}',
  'measure.empty.title': 'এখনো কোনো ফলাফল নেই',
  'measure.empty.body': 'পরীক্ষার নম্বর, ক্লাসে অবস্থান, বা যা কিছু তুমি গোনো — এখানে রাখো। অ্যাপ শুধু এখানেই বলতে পারবে কোন বিষয়ে তুমি এগোচ্ছ, কারণ প্রমাণ শুধু এখানেই আছে।',
  'measure.attach': 'ফলাফল যোগ করো',
  'measure.attachHint': 'এই সেশনের কোনো নম্বর পেয়েছ?',

  // ফোকাস — কাজ, জায়গা নয় (§47)। চলাকালীন নিচের ন্যাভবার থাকে না।
  'focus.title': 'ফোকাস',
  'focus.setup': 'কী নিয়ে কাজ করছ',
  'focus.intention': 'ঠিক কী',
  'focus.intentionPlaceholder': 'অধ্যায় ৪-এর অঙ্ক, প্রশ্ন ১-১০',
  'focus.intentionHint': 'লক্ষ্য ঠিক না করে বসলে টাইমার চলতেই থাকে, কাজ হয় না।',
  'focus.howLong': 'কতক্ষণ',
  'focus.begin': 'শুরু',
  'focus.pause': 'বিরতি',
  'focus.resume': 'আবার শুরু',
  'focus.done': 'হয়ে গেছে',
  'focus.give': 'রেকর্ড না করে থামাও',
  'focus.paused': 'বিরতিতে',
  'focus.over': 'পরিকল্পনার বেশি',
  'focus.elapsed': 'এ পর্যন্ত {minutes}',
  'focus.confirmAbandon': 'কিছু রেকর্ড না করেই থামবে?',
  'focus.keep': 'চালিয়ে যাও',

  'focus.wrapTitle': 'বন্ধ করার আগে',
  'focus.recall': 'কী মনে আছে',
  'focus.recallPlaceholder': 'না দেখে তিনটা জিনিস',
  'focus.recallHint': 'এখনই না দেখে বলাটাই সারা সপ্তাহ মনে রাখার সবচেয়ে সহজ উপায়।',
  'focus.nextStep': 'পরেরবার কোথা থেকে',
  'focus.nextStepPlaceholder': '৭ নম্বর অঙ্ক',
  'focus.nextStepHint': 'এক লাইন এখন লিখলে কাল দশ মিনিট বাঁচবে।',
  'focus.save': 'সেভ করো',
  'focus.skip': 'বাদ দাও',
  'focus.recorded': '{minutes} রেকর্ড হয়েছে',

  // রিভিশন সূচি (§48 #10)।
  'review.title': 'আবার দেখে নেওয়ার মতো',
  'review.oneTrack': '{track} — {topics}',
  'review.estimate': 'আনুমানিক {minutes}',
  'review.start': 'এখন রিভিশন',
  'review.more': 'আরও {count}টি বাকি',
  'review.overdue': { one: '{count} দিন দেরি', other: '{count} দিন দেরি' },
  'review.today': 'আজকেই',
  'review.done': 'রিভিশন হয়েছে',

  // বছরের ছবি, যাত্রার চার্টের নিচে।
  'year.title': 'এই বছর',
  'year.legendLow': 'কম',
  'year.legendHigh': 'বেশি',
  'year.perfect': { one: '{count} দিন পুরোটা রেখেছ', other: '{count} দিন পুরোটা রেখেছ' },
  'year.cell': '{date}: {promised}-এর মধ্যে {kept}টি রেখেছ',
  'year.cellRest': '{date}: কিছু নির্ধারিত ছিল না',
  'year.cellMissed': '{date}: কিছুই রাখা হয়নি',

  // চার্টের উপরের দৈনিক রেখা।
  'grid.dayLine': 'প্রতিদিন কতটুকু রেখেছ',
  'grid.dayRatio': '{date}: দিনের {value}% রাখা হয়েছে',
  'grid.dayRest': '{date}: কিছু নির্ধারিত ছিল না',
  'grid.dayToday': '{date}: আজ, এখনো চলছে',

  // ── Coach: onboarding and tips, taught on the live UI ───────────────────
  'coach.next': 'পরবর্তী',
  'coach.done': 'হয়ে গেছে',
  'coach.skip': 'বাদ দিন',
  'coach.notNow': 'এখন না',
  'coach.welcome.title': 'অগ্রযাত্রায় স্বাগতম',
  'coach.goal.title': 'শুরু হোক গন্তব্য দিয়ে',
  'coach.goal.body':
    'গন্তব্য ছাড়া অভ্যাস স্রেফ একটা কাজ। আগে ঠিক করুন আপনি কীসের দিকে এগোচ্ছেন।',
  'coach.welcome.body':
    'আপনি একটি প্রতিশ্রুতি দেন, সেটি রাখেন, আর রেকর্ড তার প্রমাণ রাখে। এক মিনিটে দেখিয়ে দিচ্ছি।',
  'coach.addHabit.body': 'কাল আপনি সত্যিই করবেন — এমন একটি কাজ দিয়ে শুরু করুন। ছোট হলেও চলবে।',
  'coach.promise.title': 'এটাই আপনার প্রতিশ্রুতি',
  'coach.promise.body':
    'কাজটি করার পরেই বৃত্তে টিক দিন — আগে নয়। রেকর্ড সত্য হলেই তার মূল্য আছে।',
  'coach.now.title': 'পড়তে বসার সময়',
  'coach.now.body': 'সেশনের সময় ধরতে বা কী পড়লেন লিখে রাখতে এখান থেকে শুরু করুন।',
  'coach.obstacle.title': 'কী বাধা হয়ে দাঁড়িয়েছিল?',
  'coach.obstacle.body':
    'একটি দিন বাদ পড়েছে — এটা স্বাভাবিক, আর কারণটা জানা থাকলে পরের সপ্তাহের পরিকল্পনা সহজ হয়।',
  'coach.obstacle.phone': 'ফোন',
  'coach.obstacle.tired': 'খুব ক্লান্ত',
  'coach.obstacle.busy': 'সময় পাইনি',
  'coach.obstacle.unclear': 'কোথা থেকে শুরু করব বুঝিনি',
  'coach.obstacle.placeholder': 'অন্য কিছু…',
  'coach.keptRate.title': 'আপনি কথা রেখেছেন',
  'coach.keptRate.body':
    'দেওয়া প্রতিশ্রুতির কতটা আপনি সত্যিই রেখেছেন। স্ট্রিকের মতো নয় — একটি খারাপ দিন এটি মুছে দেয় না।',

  // ── Goals ───────────────────────────────────────────────────────────────
  'goal.whatToward': 'আপনি কীসের দিকে এগোচ্ছেন?',
  'goal.titlePlaceholder': 'এইচএসসি ২০২৭ — গণিত',
  'goal.byWhen': 'কবের মধ্যে?',
  'goal.in3Months': '৩ মাস',
  'goal.in6Months': '৬ মাস',
  'goal.inAYear': 'এক বছর',
  'goal.pickDate': 'তারিখ বাছুন',
  'goal.start': 'শুরু',
  'goal.setDestination': 'গন্তব্য ঠিক করুন',
  'goal.pace': 'প্রতি {days} দিনে মোটামুটি একটি অংশ',
  'goal.whenHard': 'কঠিন লাগলে: {obstacle}',
  'goal.nextUpTitle': 'এরপর',
  'goal.noParts': 'এখনো কোনো অংশ নেই। ভাগ করে নিলে অগ্রগতি চোখে পড়ে।',
  'goal.addParts': 'ভাগ করুন',
  'goal.partsPlaceholder': 'প্রতি লাইনে একটি —\nঅধ্যায় ১ ভেক্টর\nঅধ্যায় ২ গতি',
  'goal.showAll': 'বাকি {count}টিই দেখান',
  'goal.showLess': 'কম দেখান',
  'jatra.pathTitle': 'কতদূর এসেছেন',
  'jatra.pathNext': '{rank} পর্যন্ত {count}',
  'jatra.pathEnd': 'পথের শেষ, আর আপনি সেটি হেঁটেছেন।',
  'jatra.alongTheWay': 'পথের ধারে',
  'jatra.goalEmpty': 'এখনো কোনো গন্তব্য নেই।',
  'jatra.setGoal': 'গন্তব্য ঠিক করুন',
  'jatra.recordTitle': 'আপনার রেকর্ড',
  'weekly.title': 'আপনার সপ্তাহ',
  'weekly.invite': '{promised}-এর মধ্যে {kept}টি রেখেছেন। দুই মিনিট ফিরে দেখবেন?',
  'weekly.start': 'রিভিউ শুরু',
  'weekly.kept': '{promised}-এর মধ্যে {kept}টি রক্ষা',
  'weekly.up': 'গত সপ্তাহের চেয়ে {value} পয়েন্ট বেশি',
  'weekly.down': 'গত সপ্তাহের চেয়ে {value} পয়েন্ট কম',
  'weekly.slipped': '{name} পিছিয়ে পড়েছে — {promised}-এর {kept}। একটু ভেবে দেখার মতো।',
  'weekly.whatGotInTheWay': 'কী বাধা হয়ে দাঁড়িয়েছিল?',
  'weekly.lastTime': 'গতবার বলেছিলেন: {obstacle}',
  'weekly.finish': 'হয়ে গেছে',
  'jatra.trendsTitle': 'ফলাফল',
  'goal.cat.academic': 'পড়াশোনা',
  'goal.cat.career': 'ক্যারিয়ার',
  'goal.cat.skill': 'দক্ষতা',
  'goal.cat.fitness': 'ফিটনেস',
  'goal.cat.business': 'ব্যবসা',
  'goal.cat.creative': 'সৃজনশীল',
  'goal.cat.personal': 'ব্যক্তিগত',
  'coach.plan.title': 'সাধারণত কখন এটি করেন?',
  'coach.plan.body':
    '{name} আপনি {time} নাগাদ করে থাকেন। সেটিকেই পরিকল্পনা বানালে আশা থেকে অভ্যাসে পরিণত হয়।',
  'coach.plan.confirm': 'হ্যাঁ, {time}',
  'coach.floor.title': 'সবচেয়ে ছোট রূপটি কী?',
  'coach.floor.body':
    'গতকাল {name} বাদ পড়েছে। দুই মিনিটের রূপটি ঠিক করে নিলে খারাপ দিনেও কথা রাখা যায়।',
  'coach.floor.placeholder': 'এক পৃষ্ঠা, বইটা খোলা…',
};
